/**
 * HostelBookingDetail.jsx — CAMP-RES-DEMO-01
 * Full booking detail: student, room, billing lines, status transitions, invoice.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, User, BedDouble, IndianRupee, CalendarCheck,
  FileText, CheckCircle2, LogIn, LogOut, XCircle, Shield,
  AlertCircle, Star, BookOpen, RotateCcw, ExternalLink,
  Landmark, TrendingUp, Clock, CreditCard,
} from "lucide-react";
import PayNowModal from "../components/payment/PayNowModal";

// ── Finance RBAC helpers ───────────────────────────────────────────────────────
const CAN_GL_VALIDATE = ["finance_manager", "cfo", "institution_admin", "super_admin"];

const GL_STATUS_CONFIG = {
  NO_JOURNAL:        { label: "No Journal",  color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  JOURNAL_DRAFT:     { label: "Draft",       color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  JOURNAL_VALIDATED: { label: "Validated",   color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  JOURNAL_APPROVED:  { label: "Approved",    color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  POSTED:            { label: "Posted",      color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
};

const INV_STATUS_CONFIG = {
  DRAFT:  { label: "Draft",  color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  ISSUED: { label: "Issued", color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  PAID:   { label: "Paid",   color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  VOID:   { label: "Void",   color: "bg-red-500/20 text-red-300 border-red-500/30" },
};

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  DRAFT:       { label: "Draft",       color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  RESERVED:    { label: "Reserved",    color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  CONFIRMED:   { label: "Confirmed",   color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  CHECKED_IN:  { label: "Checked In",  color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  CHECKED_OUT: { label: "Checked Out", color: "bg-purple-500/20 text-purple-300 border-purple-500/30" },
  CANCELLED:   { label: "Cancelled",   color: "bg-red-500/20 text-red-300 border-red-500/30" },
};

const LINE_LABELS = {
  ACCOMMODATION:   "Accommodation Charge",
  SECURITY_DEPOSIT:"Security Deposit (Refundable)",
  MEAL:            "Meal Package",
  HOUSEKEEPING:    "Housekeeping Service",
  LAUNDRY:         "Laundry Service",
  OTHER:           "Other Service",
};

// Allowed status transitions
const TRANSITIONS = {
  DRAFT:     [{ status: "RESERVED",    label: "Reserve",     icon: CheckCircle2, cls: "bg-blue-600 hover:bg-blue-500" }],
  RESERVED:  [{ status: "CONFIRMED",   label: "Confirm",     icon: CheckCircle2, cls: "bg-emerald-600 hover:bg-emerald-500" },
              { status: "CANCELLED",   label: "Cancel",      icon: XCircle,      cls: "bg-red-600 hover:bg-red-500" }],
  CONFIRMED: [{ status: "CHECKED_IN",  label: "Check In",    icon: LogIn,        cls: "bg-amber-600 hover:bg-amber-500" },
              { status: "CANCELLED",   label: "Cancel",      icon: XCircle,      cls: "bg-red-600 hover:bg-red-500" }],
  CHECKED_IN:[{ status: "CHECKED_OUT", label: "Check Out",   icon: LogOut,       cls: "bg-purple-600 hover:bg-purple-500" }],
};

export default function HostelBookingDetail() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [finStatus, setFinStatus] = useState(null);
  const [finLoading, setFinLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);

  const load = () => {
    if (!token || !id) return;
    setLoading(true);
    axios
      .get(`${API}/api/v1/hostel/bookings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { setBooking(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const loadFinStatus = () => {
    if (!token || !id) return;
    setFinLoading(true);
    axios
      .get(`${API}/api/v1/hostel/bookings/${id}/finance-status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { setFinStatus(r.data); setFinLoading(false); })
      .catch(() => setFinLoading(false));
  };

  useEffect(() => { load(); }, [token, id]);
  useEffect(() => {
    if (booking?.invoice_id && token && id) loadFinStatus();
  }, [booking?.invoice_id, token, id]);

  const handleTransition = async (newStatus) => {
    setTransitioning(true);
    try {
      await axios.patch(
        `${API}/api/v1/hostel/bookings/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Booking ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status update failed");
    } finally {
      setTransitioning(false);
    }
  };

  const handleGenerateInvoice = async () => {
    setGeneratingInvoice(true);
    try {
      const r = await axios.post(
        `${API}/api/v1/hostel/bookings/${id}/invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invoice ${r.data?.invoice_reference || ""} generated`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invoice generation failed");
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleGLRecovery = async () => {
    setRecovering(true);
    try {
      const r = await axios.post(
        `${API}/api/v1/hostel/bookings/${id}/gl-recovery`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = r.data;
      toast.success(
        d.idempotent
          ? `GL journal already exists (${d.gl_journal_status || "DRAFT"}). No duplicate created.`
          : `GL recovery: DRAFT journal created (${d.attempts_remaining} attempt${d.attempts_remaining !== 1 ? "s" : ""} remaining)`
      );
      load();
      loadFinStatus();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : (detail || e.message);
      toast.error(msg || "GL recovery failed");
    } finally {
      setRecovering(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-slate-400" data-testid="booking-detail-loading">
      <BedDouble className="w-5 h-5 mr-2 animate-pulse" /> Loading booking…
    </div>
  );

  if (!booking) return (
    <div className="p-6 text-red-400" data-testid="booking-detail-error">Booking not found.</div>
  );

  const sc = STATUS_CONFIG[booking.status] || { label: booking.status, color: "bg-slate-500/20 text-slate-300" };
  const nextTransitions = TRANSITIONS[booking.status] || [];
  const billingLines = booking.billing_lines || [];
  const totalBilling = billingLines.reduce((s, l) => s + parseFloat(l.amount || 0), 0);

  return (
    <div className="p-6 space-y-5 max-w-4xl mx-auto" data-testid="hostel-booking-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/campus/hostel/bookings")}
            className="text-slate-400 hover:text-white" data-testid="btn-back-bookings">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-amber-400" />
              <h1 className="text-xl font-bold text-white font-mono">{booking.booking_reference}</h1>
              <Badge className={`text-xs border ${sc.color}`}>{sc.label}</Badge>
            </div>
            <p className="text-slate-400 text-sm">Kaveri Bhavan Premium Residential</p>
          </div>
        </div>
        {/* Generate Invoice */}
        {["CONFIRMED", "CHECKED_IN"].includes(booking.status) && !booking.invoice_id && (
          <Button
            data-testid="btn-generate-invoice"
            onClick={handleGenerateInvoice}
            disabled={generatingInvoice}
            className="bg-blue-700 hover:bg-blue-600 text-white"
          >
            <FileText className="w-4 h-4 mr-2" />
            {generatingInvoice ? "Generating…" : "Generate Invoice"}
          </Button>
        )}
        {booking.invoice_id && (
          <div className="flex items-center gap-2 text-sm text-emerald-300 bg-emerald-900/20 border border-emerald-500/30 rounded-lg px-3 py-2"
            data-testid="invoice-generated-badge">
            <CheckCircle2 className="w-4 h-4" />
            Invoice {booking.invoice_reference || "Created"}
          </div>
        )}
        {/* PAY-UNIFIED-01: Pay Now button — shown when invoice exists and booking is payable */}
        {booking.invoice_id && ["CONFIRMED", "CHECKED_IN"].includes(booking.status) && (
          <PayNowModal
            context="HOSTEL_BOOKING"
            contextId={id}
            amount={booking.total_amount || ""}
            currency="INR"
            label="Pay Hostel Fee"
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Student Info */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4" data-testid="booking-student-card">
          <div className="flex items-center gap-2 mb-3">
            <User className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-slate-200">Student</h2>
          </div>
          <div className="space-y-1.5">
            <InfoRow label="Name" value={booking.student_name || booking.student_id} />
            <InfoRow label="Student No." value={booking.student_number || "—"} />
            <InfoRow label="Institution" value="Vaagdevi College of Engineering" />
          </div>
        </div>

        {/* Accommodation Info */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4" data-testid="booking-room-card">
          <div className="flex items-center gap-2 mb-3">
            <BedDouble className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-slate-200">Accommodation</h2>
          </div>
          <div className="space-y-1.5">
            <InfoRow label="Hostel" value={booking.hostel_name || "Kaveri Bhavan"} />
            <InfoRow label="Room" value={`Room ${booking.room_number || "—"}`} />
            <InfoRow label="Bed" value={booking.bed_label || "—"} />
            <InfoRow label="Check-in" value={booking.check_in_date || "—"} />
            <InfoRow label="Check-out" value={booking.check_out_date || "—"} />
          </div>
        </div>

        {/* Rate Info */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4" data-testid="booking-rate-card">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-slate-200">Applied Rate</h2>
          </div>
          <div className="space-y-1.5">
            <InfoRow label="Rate" value={booking.rate_name || "—"} />
            <InfoRow label="Currency" value={booking.currency_code || "INR"} />
            <InfoRow label="Total" value={`₹${(booking.total_amount || 0).toLocaleString("en-IN")}`} highlight />
            <InfoRow label="Deposit" value={`₹${(booking.security_deposit_amount || 0).toLocaleString("en-IN")}`} />
          </div>
          <p className="text-xs text-slate-500 mt-2 italic">[DEMO] Illustrative INR tariff</p>
        </div>

        {/* Status Actions */}
        {nextTransitions.length > 0 && (
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4" data-testid="booking-status-actions">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              <h2 className="text-sm font-semibold text-slate-200">Status Actions</h2>
            </div>
            <div className="flex flex-col gap-2">
              {nextTransitions.map((tr) => (
                <Button
                  key={tr.status}
                  data-testid={`btn-status-${tr.status.toLowerCase()}`}
                  onClick={() => handleTransition(tr.status)}
                  disabled={transitioning}
                  className={`${tr.cls} text-white`}
                >
                  <tr.icon className="w-4 h-4 mr-2" />
                  {transitioning ? "Updating…" : tr.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Finance Status Panel (FIN-01D) */}
      {booking.invoice_id && (
        <FinanceStatusPanel
          finStatus={finStatus}
          finLoading={finLoading}
          bookingRef={booking.booking_reference}
          userRole={user?.role || ""}
          onRetryGL={handleGLRecovery}
          recovering={recovering}
          navigate={navigate}
          glRecoveryAttempts={booking.gl_recovery_attempts || 0}
        />
      )}

      {/* Billing Lines */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl overflow-hidden" data-testid="billing-lines-card">
        <div className="px-4 py-3 border-b border-slate-700/50 flex items-center gap-2">
          <IndianRupee className="w-4 h-4 text-amber-400" />
          <h2 className="text-sm font-semibold text-slate-200">Billing Lines</h2>
        </div>
        {billingLines.length > 0 ? (
          <table className="w-full">
            <thead className="bg-slate-800/80">
              <tr>
                {["Line Type", "Description", "Period", "Qty", "Rate", "Amount"].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-xs text-slate-400 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {billingLines.map((line) => (
                <tr key={line.id} data-testid={`billing-line-${line.id}`}
                  className="bg-slate-800/40">
                  <td className="px-4 py-2.5 text-xs text-slate-300">
                    {LINE_LABELS[line.line_type] || line.line_type}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400 max-w-xs truncate">
                    {line.description || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400">
                    {line.period_from && line.period_to
                      ? `${line.period_from} → ${line.period_to}`
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-300">{line.quantity || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-300">
                    {line.unit_rate ? `₹${parseFloat(line.unit_rate).toLocaleString("en-IN")}` : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-sm font-medium text-amber-300">
                    ₹{parseFloat(line.amount || 0).toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-700/30 font-bold">
                <td colSpan={5} className="px-4 py-2.5 text-sm text-slate-300">Total Billed</td>
                <td className="px-4 py-2.5 text-sm text-amber-300" data-testid="billing-total">
                  ₹{totalBilling.toLocaleString("en-IN")}
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="px-4 py-8 text-center text-slate-500 text-sm">
            No billing lines yet. Confirm the booking to generate charges.
          </p>
        )}
      </div>

      {/* Metadata */}
      <div className="text-xs text-slate-500 flex flex-wrap gap-4">
        <span>ID: <span className="font-mono">{booking.id}</span></span>
        {booking.invoice_id && <span>Invoice: <span className="font-mono">{booking.invoice_id}</span></span>}
        {booking.created_at && <span>Created: {new Date(booking.created_at).toLocaleString()}</span>}
      </div>
    </div>
  );
}

function InfoRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-slate-400">{label}</span>
      <span className={`text-sm ${highlight ? "font-bold text-amber-300" : "text-slate-200"}`}>{value}</span>
    </div>
  );
}

// ── Finance Status Panel (FIN-01D) ────────────────────────────────────────────
function FinanceStatusPanel({ finStatus, finLoading, bookingRef, userRole, onRetryGL, recovering, navigate, glRecoveryAttempts }) {
  if (finLoading) return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 animate-pulse"
      data-testid="finance-status-loading">
      <div className="flex items-center gap-2 mb-2">
        <Landmark className="w-4 h-4 text-amber-400" />
        <span className="text-sm text-slate-400">Loading Finance Status…</span>
      </div>
    </div>
  );

  // Finance-status returns a FLAT dict (not nested)
  const invoiceId     = finStatus?.invoice_id;
  const invoiceNumber = finStatus?.invoice_number;
  const invoiceStatus = finStatus?.invoice_status;
  const amountDue     = finStatus?.amount_due;
  const amountPaid    = finStatus?.amount_paid;
  const events        = finStatus?.accounting_events || [];
  const missingConfig = finStatus?.missing_configuration || [];

  // GL lifecycle: journal_status is raw journal state
  const journalStatus = finStatus?.journal_status;
  const journalRef    = finStatus?.journal_reference;

  // Map raw journal status to composite key for badge display
  const glStatusKey = !invoiceId ? null
    : !journalStatus ? "NO_JOURNAL"
    : journalStatus === "POSTED" ? "POSTED"
    : `JOURNAL_${journalStatus}`;

  const invSc = INV_STATUS_CONFIG[invoiceStatus] || INV_STATUS_CONFIG.DRAFT;
  const glSc  = GL_STATUS_CONFIG[glStatusKey] || GL_STATUS_CONFIG.NO_JOURNAL;

  const attemptsUsed = glRecoveryAttempts || 0;
  const canRetry = CAN_GL_VALIDATE.includes(userRole)
    && !!invoiceId && glStatusKey === "NO_JOURNAL" && attemptsUsed < 3;
  const exhausted = attemptsUsed >= 3 && glStatusKey === "NO_JOURNAL";

  const LIFECYCLE = ["DRAFT", "VALIDATED", "APPROVED", "POSTED"];
  const journalPhaseMap = { DRAFT: 0, VALIDATED: 1, APPROVED: 2, POSTED: 3 };
  const curPhase = journalStatus ? (journalPhaseMap[journalStatus] ?? -1) : -1;

  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl overflow-hidden"
      data-testid="finance-status-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-700/40 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Landmark className="w-4 h-4 text-amber-400" />
          <h2 className="text-sm font-semibold text-slate-200">Finance & GL Status</h2>
          <Badge className="text-xs border bg-amber-500/10 text-amber-300 border-amber-500/20">
            FIN-01D
          </Badge>
        </div>
        <Button
          variant="ghost" size="sm"
          className="text-amber-400 hover:text-amber-300 text-xs"
          onClick={() => navigate(`/campus/hostel/gl/journals?ref=JRNL-HOSTEL-${bookingRef}`)}
          data-testid="btn-view-gl-journals"
        >
          <BookOpen className="w-3 h-3 mr-1" /> GL Journals
          <ExternalLink className="w-3 h-3 ml-1" />
        </Button>
      </div>

      <div className="p-4 space-y-4">
        {/* AR Invoice Status */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatusCard label="Invoice" value={invoiceNumber || "—"} />
          <StatusCard label="Invoice Status" badge={invSc} value={invSc.label} />
          <StatusCard label="Amount Due"
            value={amountDue != null ? `₹${parseFloat(amountDue).toLocaleString("en-IN")}` : "—"} />
          <StatusCard label="Amount Paid"
            value={amountPaid != null ? `₹${parseFloat(amountPaid).toLocaleString("en-IN")}` : "—"} />
        </div>

        {/* GL Journal Status */}
        {invoiceId && (
          <>
            <div className="border-t border-slate-700/30 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-xs font-medium text-slate-300">GL Journal Lifecycle</span>
                </div>
                {journalRef && (
                  <span className="text-xs font-mono text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded">
                    {journalRef}
                  </span>
                )}
              </div>

              {/* Lifecycle bar */}
              <div className="flex items-center gap-1" data-testid="fin-gl-lifecycle">
                {LIFECYCLE.map((s, i) => {
                  const done   = i <= curPhase;
                  const active = i === curPhase;
                  const dotColors = {
                    0: "bg-slate-400", 1: "bg-blue-400", 2: "bg-amber-400", 3: "bg-emerald-400"
                  };
                  return (
                    <React.Fragment key={s}>
                      <div className="flex flex-col items-center gap-0.5">
                        <div className={`w-3 h-3 rounded-full border border-slate-600
                          ${active ? dotColors[i] : done ? "bg-emerald-500" : "bg-slate-700"}`} />
                        <span className={`text-xs ${active ? "text-white font-semibold" : done ? "text-emerald-400" : "text-slate-600"}`}>
                          {s.charAt(0) + s.slice(1).toLowerCase()}
                        </span>
                      </div>
                      {i < LIFECYCLE.length - 1 && (
                        <div className={`flex-1 h-px ${i < curPhase ? "bg-emerald-500/40" : "bg-slate-700"} mb-3`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {curPhase === -1 && (
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                  <AlertCircle className="w-3 h-3 text-amber-400" />
                  No GL journal exists for this booking.
                  {attemptsUsed > 0 && ` (${attemptsUsed}/3 recovery attempts used)`}
                </div>
              )}
            </div>

            {/* GL Journal card info */}
            {curPhase >= 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <StatusCard label="Journal Status" badge={glSc} value={glSc.label} />
                <StatusCard label="Journal Reference" value={journalRef || "—"} mono />
                <StatusCard label="Accounting Event"
                  value={events[0]?.event_type?.replace(/_/g, " ") || "—"} />
              </div>
            )}

            {/* Missing configuration warnings */}
            {missingConfig.length > 0 && (
              <div className="space-y-1">
                {missingConfig.map((msg, i) => (
                  <div key={i} className="text-xs text-amber-400 flex items-start gap-1"
                    data-testid={`missing-config-${i}`}>
                    <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                    {msg}
                  </div>
                ))}
              </div>
            )}

            {/* Recovery actions */}
            {canRetry && (
              <div className="bg-amber-900/10 border border-amber-500/20 rounded-lg p-3 flex items-start justify-between gap-3"
                data-testid="gl-recovery-panel">
                <div>
                  <p className="text-xs font-medium text-amber-300">TX-3 GL Recovery</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    No GL journal exists. Trigger recovery to create a DRAFT journal.
                    {attemptsUsed > 0 && ` (${attemptsUsed}/3 attempts used)`}
                  </p>
                </div>
                <Button
                  size="sm"
                  className="bg-amber-700 hover:bg-amber-600 text-white text-xs px-3 shrink-0"
                  onClick={onRetryGL}
                  disabled={recovering}
                  data-testid="btn-retry-gl"
                >
                  <RotateCcw className={`w-3 h-3 mr-1 ${recovering ? "animate-spin" : ""}`} />
                  {recovering ? "Recovering…" : `Retry GL (${3 - attemptsUsed} left)`}
                </Button>
              </div>
            )}

            {exhausted && (
              <div className="bg-red-900/10 border border-red-500/20 rounded-lg p-3 text-xs text-red-400"
                data-testid="gl-recovery-exhausted">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                Maximum GL recovery attempts (3/3) exhausted. Contact a system administrator.
              </div>
            )}

            {/* Reconciliation exceptions */}
            {missingConfig.length > 0 && (
              <div className="space-y-1">
                {missingConfig.map((msg, i) => (
                  <div key={i} className="text-xs text-amber-400 flex items-start gap-1"
                    data-testid={`reconciliation-exception-${i}`}>
                    <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                    {msg}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatusCard({ label, value, badge, mono }) {
  return (
    <div className="bg-slate-700/30 rounded-lg p-2.5 border border-slate-700/30">
      <p className="text-xs text-slate-500 mb-0.5">{label}</p>
      {badge ? (
        <Badge className={`text-xs border ${badge.color}`}>{value}</Badge>
      ) : (
        <p className={`text-sm font-medium text-slate-200 ${mono ? "font-mono" : ""}`}>{value}</p>
      )}
    </div>
  );
}
